import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Camera, Utensils, Hotel, MapPin, ExternalLink } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom colored markers based on category
const createCustomIcon = (category) => {
  const colors = {
    sightseeing: '#C9B896', // Blue
    restaurant: '#FF9500',  // Orange
    hotel: '#30D158',       // Green
    activity: '#AF52DE',    // Purple
    default: '#C9B896'
  };
  
  const color = colors[category] || colors.default;
  
  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border: 3px solid #F5F5F7;
        border-radius: 50%;
        box-shadow: 0 4px 12px ${color}66;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 10px;
          height: 10px;
          background: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -20]
  });
};

// Component to auto-fit map bounds
const FitBounds = ({ places }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (places.length > 0) {
      const bounds = L.latLngBounds(
        places.map(p => [p.latitude, p.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, places]);
  
  return null;
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'sightseeing': return Camera;
    case 'restaurant': return Utensils;
    case 'hotel': return Hotel;
    default: return MapPin;
  }
};

const InteractiveMap = ({ places, language }) => {
  // Filter places with valid coordinates
  const validPlaces = useMemo(() => 
    places.filter(p => p.latitude && p.longitude),
    [places]
  );

  // Calculate center point
  const center = useMemo(() => {
    if (validPlaces.length === 0) return [0, 0];
    const avgLat = validPlaces.reduce((sum, p) => sum + p.latitude, 0) / validPlaces.length;
    const avgLng = validPlaces.reduce((sum, p) => sum + p.longitude, 0) / validPlaces.length;
    return [avgLat, avgLng];
  }, [validPlaces]);

  if (validPlaces.length === 0) {
    return null;
  }

  return (
    <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-lg" data-testid="interactive-map">
      <MapContainer
        center={center}
        zoom={8}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        {/* Dark-themed map tiles */}
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        
        <FitBounds places={validPlaces} />
        
        {validPlaces.map((place, index) => {
          const name = language === 'it' ? place.name_it : place.name_en;
          const description = language === 'it' ? place.description_it : place.description_en;
          const CategoryIcon = getCategoryIcon(place.category);
          
          return (
            <Marker
              key={place.id || index}
              position={[place.latitude, place.longitude]}
              icon={createCustomIcon(place.category)}
            >
              <Popup className="custom-popup">
                <div className="w-64 bg-[#2C2C2E] rounded-xl overflow-hidden">
                  {/* Image Preview */}
                  {place.image_url && (
                    <div className="relative h-32 overflow-hidden">
                      <img 
                        src={place.image_url} 
                        alt={name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 bg-[#2C2C2E]/80 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                        <CategoryIcon className="w-3 h-3 text-[#C9B896]" />
                        <span className="text-xs text-[#F5F5F7] capitalize">{place.category}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-sm mb-1">
                      {name}
                    </h3>
                    <p className="text-[#86868B] text-xs line-clamp-2 mb-3">
                      {description}
                    </p>
                    
                    {/* Actions */}
                    <a
                      href={`https://maps.google.com/?q=${place.latitude},${place.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#C9B896] text-xs hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {language === 'it' ? 'Apri in Google Maps' : 'Open in Google Maps'}
                    </a>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-[#2C2C2E]/90 backdrop-blur-sm rounded-xl p-3 shadow-lg">
        <p className="text-[#F5F5F7] text-xs font-medium mb-2">
          {language === 'it' ? 'Legenda' : 'Legend'}
        </p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#C9B896]"></div>
            <span className="text-[#86868B] text-xs">{language === 'it' ? 'Attrazioni' : 'Sightseeing'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#FF9500]"></div>
            <span className="text-[#86868B] text-xs">{language === 'it' ? 'Ristoranti' : 'Restaurants'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#30D158]"></div>
            <span className="text-[#86868B] text-xs">{language === 'it' ? 'Hotel' : 'Hotels'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#AF52DE]"></div>
            <span className="text-[#86868B] text-xs">{language === 'it' ? 'Attività' : 'Activities'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;
